import { useDrop } from "react-dnd";

import { calculateImageBackgroundPosition, calculateImageBackgroundSize } from "../../utils";
import { DRAGGABLE_SLICE } from "../../widget.constants";
import { usePuzzle } from "../../widget.state";
import { PuzzleRoundStatusEnum, PuzzleRoundType } from "../../widget.types";
import { PuzzleSlice } from "../puzzle-slice/puzzle-slice.component";

export function PuzzleCanvas({
  slicesPerRow,
  arrangedSlices,
  imageSlices,
  puzzleImage,
  puzzleOriginalImageWidth,
  puzzleOriginalImageHeight,
  imageWidth,
  imageHeight,
  sliceWidth,
  sliceHeight,
  setData,
  status,
}: {
  slicesPerRow: number;
  arrangedSlices: PuzzleRoundType["arrangedSlices"];
  imageSlices: { x: number; y: number }[];
  puzzleImage: string;
  puzzleOriginalImageWidth: number;
  puzzleOriginalImageHeight: number;
  imageWidth: number;
  imageHeight: number;
  sliceWidth: number;
  sliceHeight: number;
  setData: ReturnType<typeof usePuzzle>["setData"];
  status: PuzzleRoundStatusEnum;
}) {
  const updateCanvasStyleOnStateChange = (): string =>
    [
      "arcade__puzzle__canvas",
      status === PuzzleRoundStatusEnum.WON && "won",
      status === PuzzleRoundStatusEnum.LOST && "lost",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <div
      className={updateCanvasStyleOnStateChange()}
      style={{
        gridTemplateColumns: `repeat(${slicesPerRow}, 1fr)`,
        width: imageWidth,
        height: imageHeight,
      }}
    >
      {arrangedSlices.map((sliceIndex, arrangedIndex) => {
        const isCorrect = sliceIndex === arrangedIndex && status === PuzzleRoundStatusEnum.STARTED;

        const isIncorrect =
          sliceIndex !== null &&
          sliceIndex !== arrangedIndex &&
          status === PuzzleRoundStatusEnum.STARTED;

        return (
          <PuzzleCanvasSlot
            key={`arrangedSlice-${arrangedIndex}`}
            isDroppable={sliceIndex === null}
            isPlaying={
              status === PuzzleRoundStatusEnum.IDLE || status === PuzzleRoundStatusEnum.STARTED
            }
            isCorrect={isCorrect}
            isIncorrect={isIncorrect}
            targetArrangedIndex={arrangedIndex}
            width={sliceWidth}
            height={sliceHeight}
          >
            {sliceIndex !== null ? (
              <PuzzleSlice
                isDraggable={isIncorrect}
                sliceIndex={sliceIndex}
                arrangedIndex={arrangedIndex}
                backgroundSize={calculateImageBackgroundSize(
                  puzzleOriginalImageWidth,
                  puzzleOriginalImageHeight,
                  imageWidth,
                  imageHeight
                )}
                backgroundImage={`url(${puzzleImage})`}
                backgroundPosition={calculateImageBackgroundPosition(
                  puzzleOriginalImageWidth,
                  puzzleOriginalImageHeight,
                  imageWidth,
                  imageHeight,
                  imageSlices[sliceIndex]
                )}
                status={status}
                setData={setData}
                onStart={() => null}
              />
            ) : null}
          </PuzzleCanvasSlot>
        );
      })}
    </div>
  );
}

function PuzzleCanvasSlot({
  isDroppable,
  isPlaying,
  isCorrect,
  isIncorrect,
  targetArrangedIndex,
  width,
  height,
  children,
}: {
  isDroppable: boolean;
  isPlaying: boolean;
  isCorrect: boolean;
  isIncorrect: boolean;
  targetArrangedIndex: number;
  width: number;
  height: number;
  children: React.ReactNode;
}) {
  const [{ canDrop, isHovered }, dropRef] = useDrop(
    () => ({
      canDrop: () => isDroppable,
      accept: DRAGGABLE_SLICE,
      drop: () => ({ targetArrangedIndex }),
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isHovered: monitor.isOver(),
      }),
    }),
    [isDroppable, targetArrangedIndex]
  );

  const updateCanvasSlotStyleOnStateChange = (): string =>
    [
      "arcade__puzzle__canvas__slot",
      isPlaying && "arcade__puzzle__canvas__slot--playing",
      canDrop && "arcade__puzzle__canvas__slot--droppable",
      canDrop && isHovered && "arcade__puzzle__canvas__slot--hovered",
      isCorrect && "arcade__puzzle__canvas__slot--correct",
      isIncorrect && "arcade__puzzle__canvas__slot--incorrect",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <div ref={dropRef} className={updateCanvasSlotStyleOnStateChange()} style={{ width, height }}>
      {children}
    </div>
  );
}
